import type { TokenUsage, InvestigationTokenTotals } from '../../../types/backend';

/**
 * Token buckets are mutually exclusive (Bedrock's input count already excludes
 * anything the prompt cache served), so a "total" is only ever a sum of the
 * buckets it names. Sum through these helpers rather than inline, so no surface
 * quietly leaves the cache buckets out of a figure it labels a total.
 */

/** Everything the main agent spent on a turn, cache included. */
export const agentTurnTokens = (usage: TokenUsage): number =>
  (usage.input_tokens ?? 0)
  + (usage.output_tokens ?? 0)
  + (usage.cache_read_tokens ?? 0)
  + (usage.cache_write_tokens ?? 0);

/** Everything nested sub-agents spent on the same turn. */
export const subagentTurnTokens = (usage: TokenUsage): number =>
  (usage.subagent_input_tokens ?? 0)
  + (usage.subagent_output_tokens ?? 0)
  + (usage.subagent_cache_read_tokens ?? 0)
  + (usage.subagent_cache_write_tokens ?? 0);

/** The turn's full cost in tokens: agent plus sub-agents. */
export const totalTurnTokens = (usage: TokenUsage): number =>
  agentTurnTokens(usage) + subagentTurnTokens(usage);

/** Lifetime agent tokens for an investigation. */
export const agentLifetimeTokens = (totals: InvestigationTokenTotals): number =>
  (totals.input_tokens ?? 0)
  + (totals.output_tokens ?? 0)
  + (totals.cache_read_tokens ?? 0)
  + (totals.cache_write_tokens ?? 0);

/** Lifetime sub-agent tokens for an investigation. */
export const subagentLifetimeTokens = (totals: InvestigationTokenTotals): number =>
  (totals.subagent_input_tokens ?? 0)
  + (totals.subagent_output_tokens ?? 0)
  + (totals.subagent_cache_read_tokens ?? 0)
  + (totals.subagent_cache_write_tokens ?? 0);

/** Lifetime total across agent and sub-agents. */
export const totalLifetimeTokens = (totals: InvestigationTokenTotals): number =>
  agentLifetimeTokens(totals) + subagentLifetimeTokens(totals);

/**
 * Percentage of the model's context window in use, or null when either side of
 * the ratio is unknown. Null renders as no percentage — never as 0%.
 */
export const contextPercent = (
  contextTokens: number | null | undefined,
  maxContextTokens: number | null | undefined,
): number | null => {
  if (contextTokens == null || !maxContextTokens) return null;
  return Math.round((contextTokens / maxContextTokens) * 100);
};

/**
 * Context occupancy carried by the most recent measured turn, or null when the
 * conversation has none.
 *
 * The live reducer only learns the context size when a turn completes in front of
 * the user, so on a reload the chip has nothing to show until the next turn. The
 * loaded history carries the same measurement on its assistant messages, which is
 * where this reads it from.
 */
export const latestContextUsage = (
  messages: { token_usage?: TokenUsage | null }[],
): { contextTokens: number; maxContextTokens: number | null } | null => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const usage = messages[i]?.token_usage;
    if (usage?.context_tokens != null) {
      return {
        contextTokens: usage.context_tokens,
        maxContextTokens: usage.max_context_tokens ?? null,
      };
    }
  }
  return null;
};

/** Compact token count for chips and captions: 46202 -> "46.2k". */
export const formatTokens = (tokens: number): string => {
  if (tokens < 1000) return `${tokens}`;
  if (tokens < 1_000_000) return `${(tokens / 1000).toFixed(1)}k`;
  return `${(tokens / 1_000_000).toFixed(2)}M`;
};
